// ODS-EWO-033 P2. One search entry point for the whole shell. It holds no index
// of its own: the platform answers from Studio Intelligence, filtered by the
// caller's permission and the organization's entitlements, and every result
// lands back inside the application that owns the object. What it could not
// cover is stated rather than presented as "no results".

import { useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@odingard/design-system";
import type { SearchAnswer, SearchResult } from "@odingard/sdk-client";
import { shellAttentionApi } from "../lib/api";
import {
  APPLICATION_MANIFESTS,
  destinationForResolver,
  platformApplicationOf,
} from "./applications";

const MINIMUM_QUERY = 2;
const DEBOUNCE_MS = 250;

type State =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "answered"; answer: SearchAnswer }
  | { kind: "unavailable" }
  | { kind: "error"; message: string };

// An answer the shell can render, not merely one that parsed: a missing count
// or type list would throw while rendering, and a search box must never take
// the whole shell down with it (ODS-EWO-033 P6.1).
function isAnswer(data: unknown): data is SearchAnswer {
  if (!data) return false;
  const answer = data as SearchAnswer;
  return (
    Array.isArray(answer.results) &&
    Array.isArray(answer.uncoveredTypes) &&
    Boolean(answer.withheld) &&
    answer.results.every((result) => Array.isArray(result.context))
  );
}

function applicationName(application: string): string {
  const manifest = APPLICATION_MANIFESTS.find(
    (m) => platformApplicationOf(m) === application && !m.surfaceOf,
  );
  return manifest?.name ?? application;
}

function objectTypeLabel(objectType: string): string {
  return objectType.replace(/_/g, " ");
}

function withheldNotice(answer: SearchAnswer): string | null {
  const reasons: string[] = [];
  if (answer.withheld.not_entitled > 0) {
    reasons.push(
      `${answer.withheld.not_entitled} in applications this organization does not hold`,
    );
  }
  if (answer.withheld.restricted_visibility > 0) {
    reasons.push(
      `${answer.withheld.restricted_visibility} that only their own application may show`,
    );
  }
  if (reasons.length === 0) return null;
  return `Matches withheld: ${reasons.join("; ")}.`;
}

export function GlobalSearch({ orgId }: { orgId: string }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [state, setState] = useState<State>({ kind: "idle" });
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(-1);
  const container = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const listId = useId();

  const results: readonly SearchResult[] =
    state.kind === "answered" ? state.answer.results : [];

  useEffect(() => {
    const term = query.trim();
    if (term.length < MINIMUM_QUERY) {
      setState({ kind: "idle" });
      return;
    }
    if (!orgId) {
      setState({ kind: "unavailable" });
      return;
    }
    let live = true;
    setState({ kind: "loading" });
    const timer = setTimeout(() => {
      void shellAttentionApi
        .search(orgId, term)
        .then((response) => {
          if (!live) return;
          setFocused(-1);
          if (response.mode === "unavailable" || !isAnswer(response.data)) {
            setState({ kind: "unavailable" });
            return;
          }
          setState({ kind: "answered", answer: response.data });
        })
        .catch((error: unknown) => {
          if (!live) return;
          setState({
            kind: "error",
            message:
              error instanceof Error
                ? error.message
                : "Studio could not complete that search.",
          });
        });
    }, DEBOUNCE_MS);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [query, orgId]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  function go(result: SearchResult) {
    const destination = destinationForResolver(result.resolver);
    if (!destination) return;
    setOpen(false);
    navigate(destination.route);
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === "Escape") {
      setOpen(false);
      return;
    }
    if (event.key === "ArrowDown" && results.length > 0) {
      event.preventDefault();
      setOpen(true);
      setFocused((i) => (i + 1) % results.length);
    } else if (event.key === "ArrowUp" && results.length > 0) {
      event.preventDefault();
      setFocused((i) => (i <= 0 ? results.length - 1 : i - 1));
    } else if (event.key === "Enter" && focused >= 0) {
      event.preventDefault();
      const result = results[focused];
      if (result) go(result);
    }
  }

  const notice = state.kind === "answered" ? withheldNotice(state.answer) : null;

  return (
    <div className="global-search" ref={container}>
      <input
        ref={input}
        type="search"
        className="global-search__input"
        placeholder="Search Studio"
        aria-label="Search Studio"
        aria-expanded={open}
        aria-controls={listId}
        role="combobox"
        aria-autocomplete="list"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />

      {open && query.trim().length >= MINIMUM_QUERY && (
        <div className="global-search__panel">
          {state.kind === "loading" && (
            <p className="global-search__note" role="status">
              Searching…
            </p>
          )}
          {state.kind === "unavailable" && (
            <p className="global-search__note" role="status">
              Search is unavailable. Each application still opens from the switcher,
              and its own search still works.
            </p>
          )}
          {state.kind === "error" && (
            <p className="global-search__note" role="alert">
              {state.message}
            </p>
          )}
          {state.kind === "answered" && (
            <>
              <ul className="global-search__results" id={listId} role="listbox">
                {results.map((result, index) => {
                  const destination = destinationForResolver(result.resolver);
                  return (
                    <li key={`${result.objectType}:${result.objectId}`} role="none">
                      <button
                        type="button"
                        role="option"
                        aria-selected={index === focused}
                        className={`global-search__result ${
                          index === focused ? "global-search__result--focused" : ""
                        }`}
                        onKeyDown={onKeyDown}
                        onClick={() => go(result)}
                      >
                        <span className="global-search__title">{result.title}</span>
                        <span className="global-search__meta">
                          <Badge tone="neutral">
                            {objectTypeLabel(result.objectType)}
                          </Badge>
                          <span>{applicationName(result.owningApplication)}</span>
                          {destination && !destination.exact && (
                            <span className="global-search__inexact">
                              opens the application
                            </span>
                          )}
                        </span>
                        {result.context.length > 0 && (
                          <span className="global-search__context">
                            {result.context.map((c) => c.value).join(" · ")}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
              {results.length === 0 && (
                <p className="global-search__note" role="status">
                  Nothing matched in what Studio can search.
                </p>
              )}
              {notice && (
                <p className="global-search__note" role="status">
                  {notice}
                </p>
              )}
              {state.answer.uncoveredTypes.length > 0 && (
                <p className="global-search__note">
                  Not searched:{" "}
                  {state.answer.uncoveredTypes.map(objectTypeLabel).join(", ")}. Open
                  the owning application to search those.
                </p>
              )}
              {state.answer.truncated && (
                <p className="global-search__note">
                  More matched than is shown. Narrow the term.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
